import type { SaveAdapter } from './SaveAdapter';
import type { LocalAdapter } from './LocalAdapter';
import { SAVE_VERSION, type PlayerSave } from '../PlayerSave.schema';
import { applyMigrations } from '../migrations';
import { saveApi } from '@/net/api';

/**
 * ServerSaveAdapter — persistence przez backend API (sesja 34B, Step 4.D.0).
 *
 * Zastępuje LocalAdapter jako adapter SaveManagera (`src/save/index.ts`).
 * Gra ładuje/zapisuje stan z Postgres przez `/saves/*` endpointy.
 *
 * **Import jednorazowy:** przy pierwszym loadzie konta bez zainicjalizowanego
 * server save, adapter sprawdza czy istnieje lokalny IDB save (z czasów
 * single-player). Jeśli tak — migruje go do current `SAVE_VERSION` i wysyła
 * `POST /saves/import`. Po udanym imporcie **kasuje lokalny IDB save**
 * (decyzja sesji 34B) — bez tego drugi user na tym samym browserze
 * zaimportowałby ten sam save do swojego konta.
 *
 * **Lifecycle `load()`:**
 *   GET /saves/me
 *     ├─ initialized:true  → zwróć server save
 *     └─ initialized:false → lokalny IDB save istnieje?
 *          ├─ tak → migruj → POST /saves/import → clear IDB → zwróć
 *          └─ nie → zwróć null (SaveManager utworzy fresh + adapter.save = PUT)
 *
 * Wymaga ważnej sesji auth (cookie) — `main.tsx` woła `saveManager.load()`
 * DOPIERO po `waitForAuthCompletion()`.
 */
export class ServerSaveAdapter implements SaveAdapter {
  constructor(private readonly localFallback: LocalAdapter) {}

  /**
   * `save_rev` z ostatniego `GET /saves/me` (Faza 1b-1 drift-guard boot-init).
   * `SaveManager.load()` czyta to po `load()`, by zainicjować `appliedRev`.
   * `null` dla świeżego konta / braku server save (appliedRev zostaje 0).
   */
  private lastLoadedRev: number | null = null;

  getLastLoadedRev(): number | null {
    return this.lastLoadedRev;
  }

  async load(): Promise<PlayerSave | null> {
    const res = await saveApi.me();

    if (res.initialized) {
      this.lastLoadedRev = res.save_rev;
      return res.save;
    }

    // Konto bez zainicjalizowanego save — spróbuj import lokalnego IDB save.
    let local: PlayerSave | null = null;
    try {
      local = await this.localFallback.load();
    } catch (err) {
      // IDB niedostępne (incognito/quota) — traktuj jako brak lokalnego save.
      console.warn('[ServerSaveAdapter] Lokalny IDB niedostępny przy imporcie:', err);
      local = null;
    }

    if (local) {
      // Migracja do current SAVE_VERSION PRZED wysłaniem — server zod wymaga
      // aktualnego kształtu (stary v1 save by nie przeszedł walidacji).
      //
      // try/catch (Codex 34B MED 1): corrupt IDB save może wywalić
      // `applyMigrations`. NIE blokujemy wtedy boota konta serwerowego —
      // traktujemy jak brak lokalnego save (return null → fresh server save).
      let migrated: PlayerSave;
      try {
        migrated =
          local.version < SAVE_VERSION
            ? applyMigrations(local as unknown as Record<string, unknown>)
            : local;
      } catch (err) {
        console.warn(
          '[ServerSaveAdapter] Lokalny IDB save corrupt (migracja failed), pomijam import:',
          err,
        );
        return null;
      }

      // `saveApi.import` POZA try — network/500 error MA propagować
      // (to nie "corrupt save", to awaria serwera → bootstrap error).
      const result = await saveApi.import(migrated);

      if (result.imported) {
        // Skasuj lokalny IDB save (decyzja sesji 34B) — eliminuje podwójny
        // import gdyby drugi user zalogował się na tym samym browserze.
        try {
          await this.localFallback.clear();
          console.info('[ServerSaveAdapter] Import IDB → server OK, lokalny save skasowany');
        } catch (err) {
          console.warn('[ServerSaveAdapter] Import OK ale clear IDB nieudany:', err);
        }
        return migrated;
      }

      // imported:false (np. already_initialized — race / podwójne wywołanie).
      console.warn('[ServerSaveAdapter] Import skip:', result.reason);
      const after = await saveApi.me();
      if (after.initialized) {
        this.lastLoadedRev = after.save_rev;
        return after.save;
      }
      // imported:false ORAZ drugi GET nadal not-initialized = stan niespójny
      // (race między tabami?). NIE zwracamy null — SaveManager zrobiłby fresh
      // PUT nadpisując niezrozumiały kontekst. Rzucamy → bootstrap error
      // screen, user odświeży. (Codex 34B MED 2.)
      throw new Error(
        'Niespójny stan zapisu (import pominięty, konto nadal niezainicjalizowane). Odśwież stronę.',
      );
    }

    // Brak lokalnego save — SaveManager utworzy fresh i zawoła adapter.save()
    // (= PUT /saves/me, inicjalizuje server save).
    return null;
  }

  async save(state: PlayerSave): Promise<void> {
    await saveApi.put(state);
  }

  async clear(): Promise<void> {
    await saveApi.remove();
  }
}
